import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Clock, User, CheckCircle, Calendar, Filter, Loader2, Check, X, ExternalLink } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import { apiClient } from '@/services/api';
import { debugLog, errorLog } from '@/utils/logger';

interface WorkSession {
  id: string;
  userId: string;
  date: string;
  startTime: string;
  endTime: string;
  durationMinutes: number;
  description?: string;
  isApproved: boolean;
  createdAt: string;
  user?: {
    fullName: string;
    email: string;
    walletAddress?: string;
  };
}

interface RewardStatus {
  rewardId: string;
  sessionId: string;
  status: 'confirming' | 'sent' | 'failed';
  transactionHash: string;
  coinsEarned: number;
  confirmations?: {
    current: number;
    required: number;
    progress: number;
  };
  error?: string;
}

const SessionApprovalPage: React.FC = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<WorkSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'pending' | 'all'>('pending');
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const [confirmingRewards, setConfirmingRewards] = useState<Map<string, RewardStatus>>(new Map());

  useEffect(() => {
    fetchSessions();
  }, [filter]);

  // Polling effect for confirming rewards
  useEffect(() => {
    if (confirmingRewards.size === 0) return;

    debugLog(`📊 Polling ${confirmingRewards.size} confirming rewards...`);

    const pollInterval = setInterval(async () => {
      const updates = new Map(confirmingRewards);
      let hasChanges = false;

      for (const [sessionId, rewardStatus] of confirmingRewards.entries()) {
        if (rewardStatus.status !== 'confirming') continue;

        try {
          const response = await apiClient.rewards.getStatus(rewardStatus.rewardId);

          if (response.success && response.data) {
            const newStatus = response.data.status;

            if (newStatus !== 'confirming') {
              // Status changed - update UI
              updates.set(sessionId, {
                ...rewardStatus,
                status: newStatus,
                confirmations: response.data.confirmation,
                error: response.data.error,
              });
              hasChanges = true;

              // Show notification
              if (newStatus === 'sent') {
                toast.success(`✅ Payment confirmed! ${rewardStatus.coinsEarned.toFixed(2)} HEP sent`);
                debugLog(`✅ Payment confirmed for session ${sessionId}`);
              } else if (newStatus === 'failed') {
                toast.error(`❌ Payment failed: ${response.data.error || 'Unknown error'}`);
                errorLog(`❌ Payment failed for session ${sessionId}:`, response.data.error);
              }

              // Remove from confirming map after a delay
              setTimeout(() => {
                setConfirmingRewards(prev => {
                  const next = new Map(prev);
                  next.delete(sessionId);
                  return next;
                });
              }, 5000);

            } else if (response.data.confirmation) {
              // Update confirmation progress
              const currentConfirmations = response.data.confirmation.current;
              const previousConfirmations = rewardStatus.confirmations?.current || 0;

              if (currentConfirmations !== previousConfirmations) {
                updates.set(sessionId, {
                  ...rewardStatus,
                  confirmations: response.data.confirmation,
                });
                hasChanges = true;
                debugLog(`⏳ Session ${sessionId}: ${currentConfirmations}/${response.data.confirmation.required} confirmations`);
              }
            }
          }
        } catch (error: any) {
          errorLog(`Error polling reward ${rewardStatus.rewardId}:`, error);
        }
      }

      if (hasChanges) {
        setConfirmingRewards(updates);
      }
    }, 10000); // Poll every 10 seconds

    return () => clearInterval(pollInterval);
  }, [confirmingRewards]);

  const fetchSessions = async () => {
    try {
      setIsLoading(true);
      let response;
      
      if (filter === 'pending') {
        response = await apiClient.workSessions.getPending();
      } else {
        response = await apiClient.workSessions.getAll({ limit: 50 });
      }
      
      if (response.success && response.data) {
        setSessions(response.data);
      }
    } catch (error: any) {
      errorLog('Failed to fetch sessions:', error);
      toast.error('Failed to load work sessions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApproveSession = async (sessionId: string) => {
    try {
      setProcessingIds(prev => new Set([...prev, sessionId]));
      debugLog('Approving session:', sessionId);

      const response = await apiClient.workSessions.approve(sessionId);
      debugLog('Approval response:', response);

      if (response.success) {
        toast.success('✅ Work session approved!');

        // Check if transaction was submitted (async confirmation flow)
        if (response.data?.rewardId && response.data?.transactionHash) {
          const { rewardId, transactionHash, coinsEarned } = response.data;

          debugLog(`📤 Transaction submitted: ${transactionHash}`);
          toast.success(
            `Transaction submitted: ${transactionHash.slice(0, 10)}... (Confirming...)`,
            { duration: 5000 }
          );

          // Add to confirming rewards map for polling
          setConfirmingRewards(prev => new Map(prev).set(sessionId, {
            rewardId,
            sessionId,
            status: 'confirming',
            transactionHash,
            coinsEarned,
            confirmations: {
              current: 0,
              required: 3,
              progress: 0,
            },
          }));

        } else if (response.data?.distribution) {
          // Old synchronous flow (fallback)
          const { distribution } = response.data;
          if (distribution.success) {
            toast.success('Coin distribution completed!');
          } else {
            toast.error(`Approval successful, but coin distribution failed: ${distribution.error}`);
          }
        }

        // Refresh sessions list
        await fetchSessions();
      } else {
        errorLog('Approval failed:', response);
        toast.error(response.message || 'Failed to approve work session');
      }
    } catch (error: any) {
      errorLog('Failed to approve session:', error);

      // Better error handling
      if (error.response?.status === 404) {
        toast.error('Work session not found');
      } else if (error.response?.status === 403) {
        toast.error('Access denied. Admin privileges required.');
      } else if (error.response?.status === 400) {
        toast.error(error.message || 'Invalid request');
      } else if (error.code === 'ERR_NETWORK') {
        toast.error('Network error. Please check your connection.');
      } else {
        toast.error(error.message || 'Failed to approve work session');
      }
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(sessionId);
        return newSet;
      });
    }
  };

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  // Calculate duration from start/end times if durationMinutes is missing/zero
  const calculateDuration = (startTime: string, endTime: string): number => {
    const start = new Date(`2000-01-01T${startTime}:00`);
    const end = new Date(`2000-01-01T${endTime}:00`);
    return (end.getTime() - start.getTime()) / (1000 * 60); // minutes
  };

  // Calculate expected payout based on duration (1 HEP per hour)
  const calculatePayout = (session: WorkSession): number => {
    const durationMinutes = session.durationMinutes > 0 
      ? session.durationMinutes 
      : calculateDuration(session.startTime, session.endTime);
    
    const hours = durationMinutes / 60;
    const ratePerHour = 1; // 1 HEP token per hour
    return Math.round(hours * ratePerHour * 100) / 100; // Round to 2 decimal places
  };

  const renderPaymentStatus = (sessionId: string) => {
    const rewardStatus = confirmingRewards.get(sessionId);

    if (!rewardStatus) return null;

    if (rewardStatus.status === 'confirming') {
      const current = rewardStatus.confirmations?.current || 0;
      const required = rewardStatus.confirmations?.required || 3;
      const progress = rewardStatus.confirmations?.progress || 0;

      return (
        <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />
            <span className="text-sm font-medium text-blue-900">
              Confirming Payment...
            </span>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs text-blue-700">
              <span>Confirmations: {current}/{required}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="w-full bg-blue-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="flex items-center gap-1 text-xs text-blue-600">
              <ExternalLink className="h-3 w-3" />
              <Link
                to={`/explorer?hash=${rewardStatus.transactionHash}`}
                className="hover:underline"
              >
                {rewardStatus.transactionHash.slice(0, 10)}...{rewardStatus.transactionHash.slice(-8)}
              </Link>
            </div>
          </div>
        </div>
      );
    }

    if (rewardStatus.status === 'sent') {
      return (
        <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <Check className="h-4 w-4 text-green-600" />
            <span className="text-sm font-medium text-green-900">
              Payment Confirmed!
            </span>
          </div>
          <div className="mt-1 flex items-center gap-1 text-xs text-green-700">
            <span>{rewardStatus.coinsEarned.toFixed(2)} HEP sent to wallet</span>
          </div>
          <div className="mt-1 flex items-center gap-1 text-xs text-green-600">
            <ExternalLink className="h-3 w-3" />
            <Link
              to={`/explorer?hash=${rewardStatus.transactionHash}`}
              className="hover:underline"
            >
              View transaction
            </Link>
          </div>
        </div>
      );
    }

    if (rewardStatus.status === 'failed') {
      return (
        <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <X className="h-4 w-4 text-red-600" />
            <span className="text-sm font-medium text-red-900">
              Payment Failed
            </span>
          </div>
          <div className="mt-1 text-xs text-red-700">
            {rewardStatus.error || 'Transaction failed to confirm'}
          </div>
          {rewardStatus.transactionHash && (
            <div className="mt-1 flex items-center gap-1 text-xs text-red-600">
              <ExternalLink className="h-3 w-3" />
              <Link
                to={`/explorer?hash=${rewardStatus.transactionHash}`}
                className="hover:underline"
              >
                View transaction
              </Link>
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  const pendingSessions = sessions.filter(session => !session.isApproved);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin')}
          className="p-2 text-gray-500 hover:text-gray-700 touch-manipulation"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Work Session Approval</h1>
          <p className="text-gray-600 mt-1">Review and approve work sessions for payment</p>
        </div>
      </div>

      {/* Filter Controls */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center gap-4">
          <Filter className="h-5 w-5 text-gray-400" />
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('pending')}
              className={`px-4 py-2 rounded-lg text-sm font-medium touch-manipulation ${
                filter === 'pending'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Pending Only ({pendingSessions.length})
            </button>
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium touch-manipulation ${
                filter === 'all'
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              All Sessions
            </button>
          </div>
          <div className="ml-auto">
            <button
              onClick={fetchSessions}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Sessions List */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-500">Loading sessions...</p>
          </div>
        ) : sessions.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <Clock size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500 text-lg mb-2">
              {filter === 'pending' ? 'No pending sessions' : 'No sessions found'}
            </p>
            <p className="text-sm text-gray-400">
              {filter === 'pending' 
                ? 'All work sessions have been reviewed.'
                : 'No work sessions have been submitted yet.'
              }
            </p>
          </div>
        ) : (
          <div className="grid gap-4">
            {sessions.map((session) => (
              <div
                key={session.id}
                className={`bg-white rounded-lg border-2 p-4 sm:p-6 transition-colors ${
                  session.isApproved 
                    ? 'border-green-200 bg-green-50' 
                    : 'border-yellow-200 bg-yellow-50 hover:border-yellow-300'
                }`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  {/* Session Info */}
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-500" />
                        <span className="font-medium text-gray-900">
                          {session.user?.fullName || 'Unknown User'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-500" />
                        <span className="text-sm text-gray-600">
                          {format(parseISO(session.date), 'EEEE, MMM d, yyyy')}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-gray-500" />
                        <span className="text-sm font-medium text-blue-600">
                          {session.startTime} - {session.endTime} ({formatDuration(session.durationMinutes)})
                        </span>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-sm text-gray-600">
                      <div>
                        <span className="text-gray-500 text-xs sm:text-sm">Email:</span>
                        <p className="font-medium truncate" title={session.user?.email}>
                          {session.user?.email}
                        </p>
                      </div>
                      {session.user?.walletAddress && (
                        <div>
                          <span className="text-gray-500 text-xs sm:text-sm">Wallet:</span>
                          <p className="font-mono text-xs truncate" title={session.user.walletAddress}>
                            {formatAddress(session.user.walletAddress)}
                          </p>
                        </div>
                      )}
                      <div>
                        <span className="text-gray-500 text-xs sm:text-sm">Submitted:</span>
                        <p className="font-medium">{format(parseISO(session.createdAt), 'MMM d, h:mm a')}</p>
                      </div>
                      <div>
                        <span className="text-gray-500 text-xs sm:text-sm">Expected Payout:</span>
                        <p className="font-medium text-green-600">
                          {calculatePayout(session)} HEP
                          <span className="text-xs text-gray-500 ml-1">
                            (@ 1 HEP/hour)
                          </span>
                        </p>
                      </div>
                    </div>

                    {session.description && (
                      <div className="bg-white rounded-md p-3 border border-gray-200">
                        <p className="text-sm text-gray-700">{session.description}</p>
                      </div>
                    )}

                    <div className="flex items-center gap-2">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        session.isApproved 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {session.isApproved ? '✅ Approved' : '⏳ Pending Review'}
                      </span>
                      {session.durationMinutes > 480 && (
                        <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                          ⚠️ Over 8 hours
                        </span>
                      )}
                    </div>

                    {/* Payment Status */}
                    {renderPaymentStatus(session.id)}
                  </div>

                  {/* Actions */}
                  {!session.isApproved && (
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleApproveSession(session.id)}
                        disabled={processingIds.has(session.id)}
                        className="flex items-center gap-2 bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium touch-manipulation"
                      >
                        <CheckCircle size={16} />
                        {processingIds.has(session.id) ? 'Processing...' : 'Approve & Pay'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Summary */}
      {sessions.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-medium text-blue-900 mb-2">Summary</h3>
          <div className="text-sm text-blue-800 space-y-1">
            <p>Total sessions: {sessions.length}</p>
            <p>Pending approval: {pendingSessions.length}</p>
            <p>Total pending hours: {Math.round(pendingSessions.reduce((acc, s) => acc + s.durationMinutes, 0) / 60 * 100) / 100}</p>
            <p>Expected payout for pending: <span className="font-semibold">{pendingSessions.reduce((acc, s) => acc + calculatePayout(s), 0).toFixed(2)} HEP</span></p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionApprovalPage;